import { useEffect } from 'react';
import { NavMarker } from 'react-rhumb';
import { useLocation } from 'react-router-dom';
import FormattedMessage from 'I18n/components/FormattedMessage';
import RouterButton from 'ui-addon-react-router-dom/RouterButton';
import { View } from 'foundations-components/transitional/components/View';
import { H4 } from 'foundations-components/transitional/components/H4';
import { Illustration } from 'foundations-components/transitional/components/Illustration';
import { ListingPage } from 'foundations-components/transitional/components/ListingPage';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import Heading from '../../../components/Layouts/Heading';
import { SCREENS } from '../../../constants/Tracking';
import { MyLearningTabNavigation } from './MyLearningTabNavigation';
import useWootricSurvey from '../../../hooks/useWootricSurvey';
import PaginatedUICardGrid from '../../../containers/MyLearningPage/PaginatedUICardGrid';
import { MY_SAVED_ITEMS_PAGE_SIZE } from '../../savedItems';
import ApiError from '../../../components/Errors/ApiError';
import { safeSmoothScrollTo } from '../../../utils/utils';
import NoResultsError from '../../../components/Errors/NoResultsError';
import { useMyLearningParams } from '../myLearningHooks';
import ResultsStatusBar from '../../../components/ResultsStatusBar';
import { TopFilters } from '../../filters';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import UsageTracker from '../../../utils/UsageTracker';
import BlankPageMediaWrapper from '../../../components/BlankPageMediaWrapper';
import { LearningContentCard } from '../../cards';
import { useMyLearningCardModels } from '../myLearningQueries';
import { PAGE_SIZE, usePagination } from '../../pagination';
import { MY_LEARNING_FILTERS, ContentTypesFilterToMyLearningRequestMapping, MyLearningTabTypeToRequestMapping, ProgressTypeFilterToMyLearningRequestMapping } from '../myLearningConsts';
import { HasGate } from '../../accounts/components/HasGate';
import { FiltersCard } from '../../filters/components/FiltersCard';
import { PageHeader } from '../../header/components/PageHeader';
import { CertificationsFilter } from '../../filters/components/CertificationsFilter';
import { ProgressFilter } from '../../filters/components/ProgressFilter';
import { AllContentTypesFilter } from '../../filters/components/AllContentTypesFilter';
import { PageResultsCount } from '../../header/components/PageResultsCount';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { Box } from 'foundations-components/transitional/components/Box';
import { SearchFilter } from '../../filters/components/SearchFilter';
import I18n from 'I18n';
import { FilterTags } from '../../filters/components/FilterTags';
import { MobileFilterButton } from '../../filters/components/MobileFilterButton';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const useMyLearningTab = () => {
  const {
    tab,
    page,
    contentTypes,
    hasCertification,
    isAnyFilterSelected,
    progressType,
    searchTerm
  } = useMyLearningParams();
  const {
    myLearningCardModels,
    myLearningCardModelsLoading,
    myLearningCardModelsError,
    totalCount
  } = useMyLearningCardModels({
    contentTypes: contentTypes !== null && contentTypes !== void 0 && contentTypes.length ? contentTypes.map(t => ContentTypesFilterToMyLearningRequestMapping[t]).flat() : MyLearningTabTypeToRequestMapping[tab],
    offset: {
      cursor: (page - 1) * PAGE_SIZE
    },
    pageLength: PAGE_SIZE,
    progressType: progressType ? ProgressTypeFilterToMyLearningRequestMapping[progressType] : undefined,
    searchTerm,
    hasCertification,
    includePlaylistProgress: true
  });
  return {
    myLearningResults: myLearningCardModels,
    myLearningLoading: myLearningCardModelsLoading,
    myLearningError: myLearningCardModelsError,
    totalCount,
    tab,
    page,
    isAnyFilterSelected,
    searchTerm
  };
};
const ItemRightComponent = props => {
  return /*#__PURE__*/_jsx(Illustration, Object.assign({
    inlineSize: "100%",
    name: "training-academy"
  }, props));
};
const MyLearningNoResults = () => {
  const {
    tab
  } = useMyLearningParams();
  const {
    isMobile
  } = useMediaQuery();
  return /*#__PURE__*/_jsx(NavMarker, {
    name: "MYLEARNING_LISTING_NO_RESULTS",
    children: /*#__PURE__*/_jsx(View, {
      marginBlockEnd: "400",
      marginBlockStart: isMobile ? '600' : '600',
      children: /*#__PURE__*/_jsxs(BlankPageMediaWrapper, {
        "data-test-id": "my-learning-empty-state",
        itemRightComponent: ItemRightComponent,
        children: [/*#__PURE__*/_jsx(H4, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `learningCenter.myLearning.emptyState.${tab}.title`
          })
        }), /*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `learningCenter.myLearning.emptyState.${tab}.bodyOne`
          })
        }), /*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `learningCenter.myLearning.emptyState.${tab}.bodyTwo`
          })
        }), /*#__PURE__*/_jsx(RouterButton, {
          to: "/",
          use: "primary",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.myLearning.emptyState.goBack"
          })
        })]
      })
    })
  });
};
const MyLearningNoResultsWithFilter = () => {
  const location = useLocation();
  return /*#__PURE__*/_jsx(NavMarker, {
    name: "MYLEARNING_LISTING_NO_RESULTS",
    children: /*#__PURE__*/_jsx(NoResultsError, {
      resetLink: location.pathname
    })
  });
};
const MyLearningResults = ({
  totalCount,
  results
}) => {
  const {
    page
  } = useMyLearningParams();
  const {
    setPage
  } = usePagination();
  const handlePageChange = pageNumber => {
    setPage(pageNumber);
    safeSmoothScrollTo(window, 0, 0);
  };
  return /*#__PURE__*/_jsx(NavMarker, {
    name: "MYLEARNING_LISTING_LOADED",
    children: /*#__PURE__*/_jsx(PaginatedUICardGrid, {
      isLoadingPageOnDemand: true,
      limit: MY_SAVED_ITEMS_PAGE_SIZE,
      onPageChange: handlePageChange,
      page: page,
      totalCount: totalCount,
      children: results.map(item => /*#__PURE__*/_jsx(LearningContentCard, {
        item: item,
        overviewType: "my-learning"
      }, `item_${item.id}`))
    })
  });
};
const MyLearningContent = ({
  myLearningError,
  myLearningLoading,
  myLearningResults,
  totalCount,
  isAnyFilterSelected
}) => {
  return myLearningError ? /*#__PURE__*/_jsx(ApiError, {
    error: myLearningError,
    marker404: "MYLEARNING_LISTING_API_404",
    markerDefault: "MYLEARNING_LISTING_API_ERROR"
  }) : myLearningLoading ? /*#__PURE__*/_jsx(LoadingSpinner, {
    grow: true,
    minHeight: 100
  }) : myLearningResults !== null && myLearningResults !== void 0 && myLearningResults.length ? /*#__PURE__*/_jsx(MyLearningResults, {
    results: myLearningResults,
    totalCount: totalCount
  }) : isAnyFilterSelected ? /*#__PURE__*/_jsx(MyLearningNoResultsWithFilter, {}) : /*#__PURE__*/_jsx(MyLearningNoResults, {});
};
const MyLearningLegacyLayout = ({
  totalCount,
  page,
  myLearningLoading,
  tab,
  showFilters,
  isAnyFilterSelected,
  myLearningError,
  myLearningResults
}) => {
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Heading, {
      subtitle: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.heading.headingSubTitles.MY_LEARNING"
      }),
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.heading.headingTitles.MY_LEARNING"
      })
    }), /*#__PURE__*/_jsx(ResultsStatusBar, {
      cardsTotal: totalCount,
      endCard: Math.min(page * PAGE_SIZE, totalCount),
      loading: myLearningLoading,
      mobileFilterTypes: tab !== 'videos' ? MY_LEARNING_FILTERS[tab] : undefined,
      startCard: (page - 1) * PAGE_SIZE + 1
    }), /*#__PURE__*/_jsxs(ListingPage, {
      headerComponent: null,
      children: [/*#__PURE__*/_jsx(MyLearningTabNavigation, {}), showFilters && /*#__PURE__*/_jsx(TopFilters, {
        enabledFilters: MY_LEARNING_FILTERS[tab],
        showLabel: tab !== 'videos'
      }), /*#__PURE__*/_jsx(MyLearningContent, {
        isAnyFilterSelected: isAnyFilterSelected,
        myLearningError: myLearningError,
        myLearningLoading: myLearningLoading,
        myLearningResults: myLearningResults,
        totalCount: totalCount
      })]
    })]
  });
};
export const MyLearningTabs = () => {
  useWootricSurvey('CSAT');
  const {
    isMobile
  } = useMediaQuery();
  const {
    myLearningResults,
    myLearningLoading,
    myLearningError,
    totalCount,
    tab,
    page,
    isAnyFilterSelected
  } = useMyLearningTab();
  useEffect(() => {
    UsageTracker.trackStandalone('Pageview', {
      screen: SCREENS.MY_LEARNING
    });
  }, []);
  const showFilters = (myLearningResults === null || myLearningResults === void 0 ? void 0 : myLearningResults.length) || isAnyFilterSelected;
  return /*#__PURE__*/_jsx("div", {
    "data-test-id": "my-learning-page",
    children: /*#__PURE__*/_jsxs(HasGate, {
      fallback: /*#__PURE__*/_jsx(MyLearningLegacyLayout, {
        isAnyFilterSelected: isAnyFilterSelected,
        myLearningError: myLearningError,
        myLearningLoading: myLearningLoading,
        myLearningResults: myLearningResults,
        page: page,
        showFilters: !!showFilters,
        tab: tab,
        totalCount: totalCount
      }),
      gate: "Academy:UnifiedAcademy:Navigation",
      children: [/*#__PURE__*/_jsx(PageHeader, {
        subtitle: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.heading.headingSubTitles.MY_LEARNING"
        }),
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.heading.headingTitles.MY_LEARNING"
        })
      }), !isMobile && /*#__PURE__*/_jsxs(FiltersCard, {
        marginBlockEnd: "600",
        children: [/*#__PURE__*/_jsx(CertificationsFilter, {
          appearsIn: "my-learning"
        }), /*#__PURE__*/_jsx(ProgressFilter, {
          appearsIn: "my-learning"
        }), /*#__PURE__*/_jsx(AllContentTypesFilter, {
          appearsIn: "my-learning"
        })]
      }), /*#__PURE__*/_jsxs(TFlex, {
        alignItems: "center",
        gap: 16,
        marginBlockEnd: "600",
        children: [!isMobile && /*#__PURE__*/_jsx(Box, {
          flexGrow: 1,
          children: !myLearningLoading && /*#__PURE__*/_jsx(PageResultsCount, {
            page: page - 1,
            pageSize: PAGE_SIZE,
            total: totalCount
          })
        }), /*#__PURE__*/_jsx(SearchFilter, {
          appearsIn: "my-learning",
          maxInlineSize: isMobile ? undefined : '320px',
          placeholder: I18n.text('learningCenter.myLearning.searchPlaceholder')
        }), isMobile && /*#__PURE__*/_jsx(MobileFilterButton, {
          mobileFilterTypes: MY_LEARNING_FILTERS['all'],
          totalCount: totalCount
        })]
      }), /*#__PURE__*/_jsx(FilterTags, {
        appearsIn: "my-learning",
        marginBlockEnd: "500"
      }), /*#__PURE__*/_jsx(View, {
        margin: "-200",
        children: /*#__PURE__*/_jsx(MyLearningContent, {
          isAnyFilterSelected: isAnyFilterSelected,
          myLearningError: myLearningError,
          myLearningLoading: myLearningLoading,
          myLearningResults: myLearningResults,
          totalCount: totalCount
        })
      })]
    })
  });
};